import React, { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import janusApi from "../../janus-api";
import { useDispatch, useSelector } from "react-redux";
import { classNames } from "../../utils/common";
import request from "../Helper/request";

function getRandomInt() {
  return Math.floor(1000000000 + Math.random() * 9000000000);
}

/**
 * Return the requested room
 *
 * @param {Array} rooms - available rooms
 * @param {String,Integer} roomId - roomId
 * @returns {Boolean}
 */
function findRoom(rooms, roomId) {
  const id = parseInt(roomId);
  const room = rooms.find((r) => r.id === id);
  return room || {};
}

function getRoomId(rooms) {
  if (!rooms) {
    return "";
  }
  let newRoomId = getRandomInt();
  if (rooms.find((r) => r.id === newRoomId)) {
    return getRandomInt();
  }
  return newRoomId;
}

/**
 *
 * @param {String} error - the error message
 * @returns {String,undefined} - the HTML markup if error message is defined; undefined otherwise
 */
function renderError(error) {
  if (error) {
    return (
      <div
        className="bg-gray-200 border-b-4 border-secondary text-secondary px-4 py-3 mx-1 mt-2 shadow-inner"
        role="alert"
      >
        <div className="flex">
          <svg
            className="fill-current h-6 w-6 text-secondary mr-4"
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 20 20"
          >
            <path d="M2.93 17.07A10 10 0 1 1 17.07 2.93 10 10 0 0 1 2.93 17.07zm12.73-1.41A8 8 0 1 0 4.34 4.34a8 8 0 0 0 11.32 11.32zM9 11V9h2v6H9v-4zm0-6h2v2H9V5z" />
          </svg>
          <div>
            <p className="font-bold">{error}</p>
          </div>
        </div>
      </div>
    );
  }
}

function renderSuccess(roomcreated) {
  if (roomcreated) {
    return (
      <div>
        <label className="form-label form-element mt-2" htmlFor="roomUrl">
          Room URL
        </label>
        <div
          className="bg-gray-200 border-b-4 border-green-600 text-green-600 px-4 py-3 mx-1 mt-2 shadow-inner"
          role="alert"
        >
          <div className="flex">
            <div style={{ fontSize: "14px" }}>
              {window.location.origin + "/#/room/" + roomcreated.roomData.room}
            </div>
          </div>
        </div>
      </div>
    );
  }
}

function CreateRoomForm() {
  const dispatch = useDispatch();
  const [rooms, setRooms] = useState([]);
  const [roomId, setRoomId] = useState("");
  const [selectedRoom, setSelectedRoom] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isError, setIsError] = useState(false);
  const [roomcreated, setRoomCreated] = useState("");
  const previousRoom = useSelector((state) => state.room);
  const { register, handleSubmit, reset, errors } = useForm();
  const { username: previousUsername, error } = previousRoom;

  /**
   * Handles the form submit
   *
   * @param {Function} reset - the dispatch function available in the Redux store
   * @returns {Function}
   */
  function onSubmit(reset) {
    return function (data) {
      setIsSubmitting(true);
      setRoomCreated("");

      janusApi
        .createRoom({
          room: data.roomId,
          pin: data.pin,
          secret: data.secret.toLowerCase(),
        })
        .then((room) => {
          sendToKbport({
            roomData: room,
            RoomPin: data.pin,
            AdminSecretKey: data.secret.toLowerCase(),
          });
          reset();
          setRoomId(getRoomId(rooms));
        })
        .catch((err) => {
          setIsError("Something went wrong");
        });

      setIsSubmitting(false);
    };
  }

  const sendToKbport = ({ roomData, ...roomSecrets }) => {
    return request
      .post({
        url: "\\api//Telehealth//PostMeeting",
        body: {
          RoomURL: window.location.origin + "/#/room/" + roomData.room,
          ...roomSecrets,
        },
      })
      .then(() => {
        setRoomCreated({ roomData, ...roomSecrets });
      })
      .catch((err) => {
        throw err;
      });
  };

  useEffect(() => {
    janusApi.getRooms().then((rooms) => {
      setRooms(rooms);
      let roomId;
      if (previousRoom.roomId) {
        roomId = previousRoom.roomId;
      } else if (rooms.length > 0) {
        roomId = rooms[0].id;
      }
      reset({ room: roomId });
      setSelectedRoom(findRoom(rooms, roomId));
      setRoomId(getRoomId(rooms));
    });
  }, []);

  return (
    <form className="mt-2" onSubmit={handleSubmit(onSubmit(reset))}>
      {renderError(isError)}
      <div className="flex flex-col sm:flex-row lg:flex-col">
        <div className="form-element">
          <label className="form-label" htmlFor="roomId">
            Room Id
          </label>
          <input
            id="roomId"
            type="number"
            placeholder="roomId"
            name="roomId"
            className="form-input"
            ref={register({
              required: {
                value: true,
                message: "RoomId is required",
              },
            })}
            disabled
            defaultValue={roomId}
          />
        </div>

        <div className="form-element">
          <label className="form-label" htmlFor="roomPin">
            Pin to enter room
          </label>
          <input
            id="password"
            name="pin"
            type="password"
            ref={register({
              required: {
                value: true,
                message: "Pin is required",
              },
              minLength: {
                value: 5,
                message: "Pin must be at least 5 characters",
              },
            })}
            className="form-input"
            required
          />
          {errors.pin && (
            <div className="error" style={{ color: "#f15a5c" }}>
              {errors.pin.message}
            </div>
          )}
        </div>
        <div className="form-element">
          <label className="form-label" htmlFor="roomSecret">
            Secret key to perform admin roles
          </label>
          <input
            id="secret"
            name="secret"
            type="password"
            ref={register({
              required: {
                value: true,
                message: "Secret key is required",
              },
              minLength: {
                value: 5,
                message: "Secret key must be at least 5 characters",
              },
            })}
            className="form-input"
            required
          />
          {errors.secret && (
            <div className="error" style={{ color: "#f15a5c" }}>
              {errors.secret.message}
            </div>
          )}
        </div>
        {renderSuccess(roomcreated)}
      </div>
      <div className="flex">
        <div
          className={classNames(
            "form-element self-end",
            selectedRoom.pinRequired ? "w-1/2" : "w-full mt-6"
          )}
        >
          <input
            className="form-btn"
            type="submit"
            value={roomcreated ? "Create Another Room" : "Create"}
            disabled={isSubmitting}
          />
        </div>
      </div>
    </form>
  );
}

export default CreateRoomForm;
